import {
  DialogContent,
  Dialog,
  DialogProps,
  useEventCallback,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import { PmiButton } from 'shell/components/shared/button';
import { styled } from '@mui/styles';
import { memo, useMemo, useState, useEffect } from 'react';
import { useFormik } from 'formik';
import {
  EditResidenceVM,
  PracticeEditResidenceCadastralFields,
} from 'practice/view-models/practice-edit-residence';
import Form from './form';
import * as yup from 'yup';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { PracticeAddress } from 'shell/models/practice-address';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { regexOnlyNumber } from 'shell/constants/regex';
import { HttpResponse } from 'shell/models/http-response';
import { TypeRegister } from 'practice/constants/type-register';
import ModalWarning from 'shell/components/shared/modal-warning';
import { ObjectShape } from 'yup/lib/object';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { useParams } from 'react-router-dom';
import { CadastralFieldsVisibility } from 'shell/models/practice';
import { AxiosResponse } from 'axios';
import { ISTAT_MILANO } from 'shell/constants/istat-milano';
import { ItalianVM } from 'shell/view-models/register-office-practice-vm';

export const PmiDialog = styled((props: DialogProps) => <Dialog {...props} />)((props) => ({
  '& .MuiDialog-paper': props.fullScreen
    ? {
        minWidth: 'unset',
        maxWidth: '100%',
      }
    : {
        minWidth: '50rem',
        maxWidth: '800px',
      },
}));

const validationSchema = (isCadastralFieldsMandatory?: boolean, typeRegister?: TypeRegister) => {
  let schema: ObjectShape = {};

  const isTypeRegisterImmigration = typeRegister === TypeRegister.Immigration;

  const isEmigration = typeRegister === TypeRegister.Emigration;

  schema = {
    checkedResidence: yup.boolean().nullable(),
    italian: yup.object().shape({
      section: yup.string().nullable(),
      sheet: yup.string().nullable(),
      particle: yup.string().nullable(),
      sub: yup.string().nullable(),
      cat: yup.string().nullable(),
      municipality: yup.object().nullable(),
      cap: yup.string().nullable(),
      addressAuto: yup.object().nullable(),
      number: yup.string().nullable(),
      houseOccupation: yup.string().nullable(),
      officeContract: yup.string().nullable(),
      numberContract: yup.string().matches(regexOnlyNumber, 'Il campo è numerico').nullable(),
      dateContract: yup.date().nullable().typeError('Data non valida'),
    }),
  };
  return yup
    .object({
      ...schema,
    })
    .test('municipality', 'Controllo', (values) => {
      const castedValues = values as unknown as EditResidenceVM;

      if (!castedValues.checkedResidence && !castedValues.italian?.municipality?.description) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'italian.municipality');
      }

      return true;
    })
    .test('addressAuto', 'Controllo', (values) => {
      const castedValues = values as unknown as EditResidenceVM;

      if (
        !castedValues.checkedResidence &&
        !castedValues.italian?.addressAuto?.description &&
        !isEmigration
      ) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'italian.addressAuto');
      }

      return true;
    })
    .test('cap', 'Controllo', (values) => {
      const castedValues = values as unknown as EditResidenceVM;

      if (!castedValues.checkedResidence && !castedValues.italian?.cap && !isEmigration) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'italian.cap');
      }

      return true;
    })
    .test('number', 'Controllo', (values) => {
      const castedValues = values as unknown as EditResidenceVM;

      if (!castedValues.checkedResidence && !castedValues.italian?.number && !isEmigration) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'italian.number');
      }

      return true;
    })
    .test('sheet', 'Controllo', (values) => {
      const castedValues = values as unknown as EditResidenceVM;

      if (
        castedValues.italian?.section?.toUpperCase() === 'NC' ||
        castedValues.italian?.section?.toUpperCase() === 'NP'
      ) {
        return true;
      }

      if (
        !castedValues.checkedResidence &&
        !castedValues.italian?.sheet &&
        isCadastralFieldsMandatory &&
        isTypeRegisterImmigration &&
        !castedValues?.italian?.isHomeless
      ) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'italian.sheet');
      }

      return true;
    })
    .test('particle', 'Controllo', (values) => {
      const castedValues = values as unknown as EditResidenceVM;

      if (
        castedValues.italian?.section?.toUpperCase() === 'NC' ||
        castedValues.italian?.section?.toUpperCase() === 'NP'
      ) {
        return true;
      }

      if (
        !castedValues.checkedResidence &&
        !castedValues.italian?.particle &&
        isCadastralFieldsMandatory &&
        isTypeRegisterImmigration &&
        !castedValues?.italian?.isHomeless
      ) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'italian.particle');
      }

      return true;
    })
    .test('sub', 'Controllo', (values) => {
      const castedValues = values as unknown as EditResidenceVM;

      if (
        castedValues.italian?.section?.toUpperCase() === 'NC' ||
        castedValues.italian?.section?.toUpperCase() === 'NP'
      ) {
        return true;
      }

      if (
        !castedValues.checkedResidence &&
        !castedValues.italian?.sub &&
        isCadastralFieldsMandatory &&
        isTypeRegisterImmigration &&
        !castedValues?.italian?.isHomeless
      ) {
        return new yup.ValidationError('Campo obbligatorio', undefined, 'italian.sub');
      }

      return true;
    })
    .test('', 'Controllo Agenzia entrate', (values) => {
      const castedValues = values as unknown as EditResidenceVM;

      if (
        castedValues.italian?.houseOccupationType !== '2' &&
        castedValues.italian?.houseOccupationType !== '4'
      ) {
        return true;
      }

      const elements = ['numberContract', 'dateContract', 'officeContract'] as Array<
        keyof ItalianVM
      >;

      const hasElementPresent = elements.every((item) => {
        if (!castedValues.italian) {
          return false;
        }

        return !!castedValues.italian[item];
      });

      if (!castedValues.checkedResidence && !hasElementPresent) {
        const errors = elements
          .map((key) => {
            const value = (castedValues?.italian || {})[key];
            if (value) {
              return null;
            } // Successful validation, no error

            return new yup.ValidationError('Il campo è obbligatorio', value, `italian.${key}`);
          })
          .filter((item) => !!item) as unknown as Array<yup.ValidationError>;
        if (errors.length === 0) {
          return true;
        }
        return new yup.ValidationError(errors);
      }

      return true;
    });
};

interface ModalEditResidenceProps {
  open: boolean;
  onClose: (open: boolean) => void;
  onConfirm: (values: EditResidenceVM) => void;
  initialValues?: EditResidenceVM;
  codeEnte?: string;
  typeRegister?: string;
  intestatarioIsAnpr?: boolean;
  yearId?: string;
  progressive?: string;
  typePractice?: string;
}

const ModalEditResidenceComponent = ({
  open,
  onClose,
  onConfirm,
  initialValues,
  codeEnte,
  typeRegister,
  yearId,
  progressive,
  intestatarioIsAnpr,
}: ModalEditResidenceProps): React.ReactElement => {
  const { userProfile } = useUserProfile();

  const [openWarning, setOpenWarning] = useState({ open: false, message: '', values: {} });

  const params = useParams();

  const [indirizziControloDati, getIndirizziControlloDati] = useHttpFetch<
    HttpResponse<CadastralFieldsVisibility>,
    PracticeEditResidenceCadastralFields
  >(
    `${__PRACTICE_API_URL__}/pratiche/indirizzi/controllo-dati/${params.codiceEnte}-${params.idAnnoPratica}-${params.tipoRegistro}-${params.progressivoPratica}`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: (response: AxiosResponse<HttpResponse<CadastralFieldsVisibility>>) => {
        const data = {
          isCadastralFieldsMandatory: response?.data?.payload?.datiObbligatori,
        };
        return data;
      },
    }
  );

  useEffect(() => {
    if (typeRegister === TypeRegister.Immigration) {
      getIndirizziControlloDati();
    }
  }, [typeRegister, getIndirizziControlloDati]);

  const isCambioResidenza = useMemo(() => {
    if (typeRegister === TypeRegister.Residence) {
      return true;
    }

    return false;
  }, [typeRegister]);

  const isImmigrazione = useMemo(() => {
    if (typeRegister === TypeRegister.Immigration) {
      return true;
    }

    return false;
  }, [typeRegister]);

  const isEmigrazione = useMemo(() => {
    if (typeRegister === TypeRegister.Emigration) {
      return true;
    }

    return false;
  }, [typeRegister]);

  const constrolData = useHttpClient(
    `${__PRACTICE_API_URL__}/pratiche/registrazione-nuovo-indirizzo/controlli`,
    {
      options: { method: 'POST' },
    }
  );

  const onSubmit = useEventCallback((values: EditResidenceVM) => {
    const data = {
      pratica: {
        key: {
          codiceEnte: codeEnte || undefined,
          tipoRegistro: typeRegister || undefined,
          idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
          progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
        },
      },
      indirizzo:
        values?.italian && !values?.checkedResidence
          ? {
              citta: values?.italian.municipality?.description || undefined,
              provincia: values?.italian.municipality?.province || undefined,
              cap: values?.italian?.cap || undefined,
              codVia: values?.italian?.addressAuto?.code || undefined,
              nomeVia: values?.italian?.addressAuto?.description || undefined,
              numeroCivico: values?.italian?.number || undefined,
              esponente: values?.italian?.exponent || undefined,
              // lettera: values?.italian?.letter || undefined,
              sezione: values?.italian?.section || undefined,
              foglio: values?.italian?.sheet || undefined,
              particellaMappale: values?.italian?.particle || undefined,
              subalterno: values?.italian?.sub || undefined,
              categoriaCatastale: values?.italian?.cat || undefined,
              flagIgnotaDimora: values?.italian?.isHomeless ? '1' : '0',
              //nuovi campi
              tipoOccupazione: values?.italian?.houseOccupationType || undefined,
              sedeAgenziaEntrate: values?.italian?.officeContract || undefined,
              dataRegistrazioneAgenziaEntrate:
                values?.italian?.dateContract?.toFormat('yyyy-MM-dd') || undefined,
              numeroRegistrazioneAgenziaEntrate: values?.italian?.numberContract || undefined,
            }
          : undefined,
      indirizzoEstero:
        values?.foreign && values?.checkedResidence
          ? {
              statoEstero: values?.foreign?.foreignState?.province || undefined,
              comune: values?.foreign?.municipality?.description || undefined,
              codComune: values?.foreign?.municipality?.id
                ? Number.parseInt(values?.foreign?.municipality?.id, 10)
                : undefined,
              contea: values?.foreign?.municipality?.province || undefined,
              cap: values?.foreign?.cap || undefined,
              indirizzo: values?.foreign?.address || undefined,
              civico: values?.foreign?.civicNumber || undefined,
              codiceConsolato: values?.foreign?.consulateCode?.id || undefined,
              codiceTerritorio: values?.foreign?.territory?.id || undefined,
            }
          : undefined,
    };

    (constrolData({ data }) as ResponsePromise<HttpResponse<PracticeAddress>>).then((response) => {
      const castedResponse = response?.data?.payload as PracticeAddress;

      if ((castedResponse?.warning || []).length > 0) {
        setOpenWarning({ open: true, message: castedResponse?.warning?.join(', ') || '', values });

        return;
      }

      onConfirm(values);
    });
  });

  const formContext = useFormik<EditResidenceVM>({
    initialValues: {
      checkedResidence: initialValues?.checkedResidence
        ? true
        : (isCambioResidenza && intestatarioIsAnpr) || isEmigrazione
        ? true
        : // : isImmigrazione
          // ? false
          false,
      flagAttualeResidenza: initialValues?.flagAttualeResidenza || false,
      italian: {
        section: undefined,
        sheet: undefined,
        particle: undefined,
        sub: undefined,
        cat: undefined,
        cap: undefined,
        addressAuto: undefined,
        number: undefined,
        ...(initialValues?.italian || {}),
        municipality:
          isCambioResidenza && !intestatarioIsAnpr
            ? {
                id: ISTAT_MILANO,
                description: 'MILANO',
                provinceCode: '15',
                province: 'MI',
              }
            : initialValues?.italian?.municipality || {
                description: userProfile.datiLogin?.nomeComune || '',
                province: userProfile.datiLogin?.siglaProvincia || '',
                id: userProfile.datiLogin?.codiceIstat || '',
              },
      },
      foreign: initialValues?.foreign || undefined,
    },
    validationSchema: validationSchema(
      (indirizziControloDati?.data as PracticeEditResidenceCadastralFields)
        ?.isCadastralFieldsMandatory,
      typeRegister as TypeRegister
    ),
    onSubmit,
  });

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  const onChange = useEventCallback(
    (key: keyof EditResidenceVM, value: EditResidenceVM[keyof EditResidenceVM]): void => {
      formContext.setFieldValue(key, value);
    }
  );

  const onCloseWarning = useEventCallback(() => {
    const values: EditResidenceVM = openWarning.values as EditResidenceVM;

    setOpenWarning({ open: false, message: '', values: {} });

    onConfirm(values);
  });

  return (
    <>
      <PmiDialog
        fullScreen={fullScreen}
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <PmiDialogTitle id="alert-dialog-title" backgroundColor="primary" color="white">
          Residenza
        </PmiDialogTitle>
        <DialogContent style={{ padding: '30px 24px' }}>
          <Form
            onChange={onChange}
            values={formContext.values}
            errors={formContext.errors}
            touched={formContext.touched}
            isCambioResidenza={isCambioResidenza}
            isImmigrazione={isImmigrazione}
            intestatarioIsAnpr={intestatarioIsAnpr}
            isEmigr={typeRegister === TypeRegister.Emigration}
          />
        </DialogContent>
        <DialogActions>
          <PmiButton variant="contained" color="primary" onClick={() => formContext.handleSubmit()}>
            Conferma
          </PmiButton>
          <PmiButton variant="outlined" color="primary" type="button" onClick={() => onClose(true)}>
            Chiudi
          </PmiButton>
        </DialogActions>
      </PmiDialog>

      <ModalWarning
        open={openWarning.open}
        onClose={onCloseWarning}
        message={openWarning.message}
        withAnnulment
        onAnnulment={() => setOpenWarning({ open: false, message: '', values: {} })}
        nameButton="Prosegui"
      />
    </>
  );
};

ModalEditResidenceComponent.displayName = 'Modal new residence';

const ModalEditResidence = memo(ModalEditResidenceComponent);

export default ModalEditResidence;
